// src/context/CartContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useGlobalContext } from './GlobalContext';
import { useParams } from 'react-router-dom';
import {
  calculateCardWeight,
  createAddressKey,
  applyDiscountToAmount,
  isCartDiscountApplicable,
  isCardDiscountApplicable,
  calculateItemDiscountedAmount
} from '../utils/cartUtils';

export const CartContext = createContext();

export const useCartContext = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { store, applyDiscount,selectedStore, setSelectedStore } = useGlobalContext();
  const { store: storeParam } = useParams();

  const [selectedStoreCards, setSelectedStoreCards] = useState([]);

  const [haveDiscount, setHaveDiscount] = useState(false);
  const [cartDiscountCondition, setCartDiscountCondition] = useState(null);
  const [discountType, setDiscountType] = useState(null);

  const [promoCode, setPromoCode] = useState(() => sessionStorage.getItem('promoCode') || '');
  const [promoDiscount, setPromoDiscount] = useState(() => {
    const saved = sessionStorage.getItem('promoDiscount');
    return saved ? JSON.parse(saved) : null;
  });
  const [promoError, setPromoError] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const [totalAmounts, setTotalAmounts] = useState(0);
  const [totalAmountsAfterDiscount, setTotalAmountsAfterDiscount] = useState(0);
  const [totalPort, setTotalPort] = useState(0);
  const [totalFinal, setTotalFinal] = useState(0);

  const initialCart = JSON.parse(sessionStorage.getItem('cart')) || {
    items: [],
    delivery: {},
    option: {},
    itemsByAddress: {},
    totalWeightByAddress: {},
    itemsByAmount: {}
  };

  const [cart, setCart] = useState(initialCart);
  const [cartItemsCountMax, setCartItemsCountMax] = useState(0);
  const [cartAmountMax, setCartAmountMax] = useState(0);
  useEffect(() => {
    sessionStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);



  useEffect(() => {
    if (store && store.cards) {
      setCartItemsCountMax(store.cartItemsCountMax || 0);
      setCartAmountMax(store.cartAmountMax || 0);
      const filteredCards = store.cards.filter(
        card => card.store === selectedStore || card.store === 'both'
      );
      setSelectedStoreCards(filteredCards);
    } else {
      setSelectedStoreCards([]);
    }
  }, [store, selectedStore]);

  const updateCard = (index, newCardData) => {
    setCart(prevCart => {
      const newItems = [...prevCart.items];
      newItems[index] = {
        ...newItems[index],
        ...newCardData
      };
      return {
        ...prevCart,
        items: newItems
      };
    });
  };

  const calculateShippingTotal = useCallback(() => {
    let shippingTotal = 0;

    if (selectedStore !== "physical") {
      return shippingTotal;
    }

    Object.entries(cart.delivery || {}).forEach(([addressKey, deliveryOption]) => {
      if (deliveryOption && cart.totalWeightByAddress?.[addressKey]) {
        const weight = cart.totalWeightByAddress[addressKey];

        if (!deliveryOption.prices || !Array.isArray(deliveryOption.prices) || deliveryOption.prices.length === 0) {
          console.warn(`[calculateShippingTotal] Pas de prix définis pour l'option de livraison ${deliveryOption.name}`);
          return;
        }

        let selectedPrice = null;
        let maxWeightPrice = null;

        deliveryOption.prices.forEach(price => {
          if (weight <= price.weight && (selectedPrice === null || selectedPrice.weight > price.weight)) {
            selectedPrice = price;
          }

          if (maxWeightPrice === null || maxWeightPrice.weight < price.weight) {
            maxWeightPrice = price;
          }
        });

        const priceToAdd = selectedPrice
          ? selectedPrice.price
          : (maxWeightPrice ? maxWeightPrice.price : 0);

        shippingTotal += priceToAdd;
      } else {
        console.warn(`[calculateShippingTotal] Données manquantes pour l'adresse ${addressKey}`);
      }
    });

    return shippingTotal;
  }, [cart.delivery, cart.totalWeightByAddress, selectedStore]);

  const calcCartTotals = useCallback(() => {
    try {
      const newItems = cart.items.map(item => ({
        ...item,
        amountAfterDiscount: item.amount,
        discountCondition: null
      }));
  
      const proDiscount = store?.professionalUser?.discount || store?.proSignboardDiscount || 0;

      let totalBeforeDiscounts = 0;
      let optionsPrice = 0;
      let haveDiscountFlag = false;
      let cartDiscount = null;
      let discountTypeValue = 'discount';
  
      newItems.forEach(item => {
        totalBeforeDiscounts += (item.qty || 1) * item.amount;
      });
  
      if (selectedStore === "physical") {
        Object.entries(cart.option || {}).forEach(([keyCart, cardOptions]) => {
          Object.entries(cardOptions).forEach(([keyOption, isSelected]) => {
            if (isSelected === true) {
              let optionFound = false;
              let optionPrice = 0;
  
              if (Array.isArray(store?.physical_store?.card_options)) {
                const optionByAnchor = store.physical_store.card_options.find(opt => opt.anchor === keyOption);
                if (optionByAnchor) {
                  optionFound = true;
                  optionPrice = optionByAnchor.price || 0;
                }
  
                if (!optionFound) {
                  const index = parseInt(keyOption, 10);
                  if (!isNaN(index) && index < store.physical_store.card_options.length) {
                    const optionByIndex = store.physical_store.card_options[index];
                    optionFound = true;
                    optionPrice = optionByIndex.price || 0;
                  }
                }
              }
  
              if (optionFound) {
                optionsPrice += optionPrice;
              }
            }
          });
        });
  
        totalBeforeDiscounts += optionsPrice;
      }
  
      // Déterminer la source de remise : code promo > remise pro > remise store
      const activeDiscount = promoDiscount || null;

      if (activeDiscount && activeDiscount.conditions) {
        // Code promo actif : il écrase toute autre promo
        const promoType = activeDiscount.type || 'discount';
        const promoLimit = activeDiscount.limit || 0;
        let promoCartCondition = null;

        activeDiscount.conditions.forEach(condition => {
          if (condition.thresholdType === 'cart' && isCartDiscountApplicable(condition, totalBeforeDiscounts)) {
            promoCartCondition = condition;
          } else if (condition.thresholdType === 'card') {
            newItems.forEach((item, index) => {
              if (isCardDiscountApplicable(condition, item)) {
                haveDiscountFlag = true;
                const newItem = { ...item };
                newItem.discountCondition = condition;
                newItem.amountAfterDiscount = calculateItemDiscountedAmount(
                  item,
                  condition,
                  promoType
                );
                newItems[index] = newItem;
              }
            });
          }
        });

        if (promoCartCondition) {
          haveDiscountFlag = true;
          cartDiscount = promoCartCondition;
          discountTypeValue = promoType;
        }
      } else if (proDiscount > 0) {
        haveDiscountFlag = true;
        discountTypeValue = 'discount';
        cartDiscount = {
          amount: 0,
          value: proDiscount,
          thresholdType: "cart",
          valueType: "percent"
        };
      } else if (!store?.b2b2c && store?.discount && applyDiscount) {
        store.discount.conditions.forEach(condition => {
          if (condition.thresholdType === 'cart' && isCartDiscountApplicable(condition, totalBeforeDiscounts)) {
            haveDiscountFlag = true;
            cartDiscount = condition;
            discountTypeValue = store.discount.type;
          }
          else if (condition.thresholdType === 'card') {
            newItems.forEach((item, index) => {
              if (isCardDiscountApplicable(condition, item)) {
                haveDiscountFlag = true;
                const newItem = { ...item };
                newItem.amountAfterDiscount = calculateItemDiscountedAmount(
                  item,
                  condition,
                  store.discount.type
                );
                newItem.discountCondition = condition;
                newItems[index] = newItem;
              }
            });
          }
        });
      }

      let totalAfterItemDiscounts = 0;
      newItems.forEach(item => {
        totalAfterItemDiscounts += (item.qty || 1) * (item.amountAfterDiscount || item.amount);
      });
      totalAfterItemDiscounts += optionsPrice;

      let totalAfterDiscount = totalAfterItemDiscounts;

      if (haveDiscountFlag && cartDiscount) {
        if (!activeDiscount && proDiscount > 0) {
          if (cartDiscount.valueType === 'percent') {
            const discountAmount = totalAfterItemDiscounts * cartDiscount.value / 100;
            totalAfterDiscount = totalAfterItemDiscounts - discountAmount;
          } else if (cartDiscount.valueType === 'amount') {
            totalAfterDiscount = totalAfterItemDiscounts - cartDiscount.value;
          }
        } else {
          const discountSource = activeDiscount || store?.discount || {};
          totalAfterDiscount = applyDiscountToAmount(
            totalAfterItemDiscounts,
            cartDiscount,
            discountSource.type || 'discount',
            discountSource.limit || 0
          );
        }
      }
  
      const needsUpdate =
        JSON.stringify(cart.items) !== JSON.stringify(newItems) ||
        cart.haveDiscount !== haveDiscountFlag ||
        JSON.stringify(cart.cartDiscountCondition) !== JSON.stringify(cartDiscount) ||
        cart.discountType !== discountTypeValue;
  
      if (needsUpdate) {
        setCart(prev => ({
          ...prev,
          items: newItems,
          haveDiscount: haveDiscountFlag,
          cartDiscountCondition: cartDiscount,
          discountType: discountTypeValue
        }));
  
        setHaveDiscount(haveDiscountFlag);
        setCartDiscountCondition(cartDiscount);
        setDiscountType(discountTypeValue);
      }
  
      setTotalAmounts(totalBeforeDiscounts);
      setTotalAmountsAfterDiscount(totalAfterDiscount);
  
      const portTotal = calculateShippingTotal();
      setTotalPort(portTotal);
      setTotalFinal(totalAfterDiscount + portTotal);
  

  
    } catch (error) {
      console.error('[calcCartTotals] Erreur:', error);
    }
  }, [cart.items, cart.option, cart.haveDiscount, cart.delivery, cart.cartDiscountCondition, cart.discountType, store, applyDiscount, selectedStore, cart.totalWeightByAddress, calculateShippingTotal, promoDiscount]);

  const calculateBaseCardWeight = useCallback((storeData) => {
    let cardWeight = (storeData?.physical_store?.card_weight && storeData.physical_store.card_weight > 0)
      ? storeData.physical_store.card_weight
      : 5;

    if (storeData?.physical_store?.use_sleeve && storeData.physical_store.sleeve_weight > 0) {
      cardWeight += storeData.physical_store.sleeve_weight;
    }

    return cardWeight;
  }, []);

  const getSelectedKeyOption = useCallback((keyCart) => {
    let selectedKey = null;
    if (cart.option?.[keyCart]) {
      Object.entries(cart.option[keyCart]).forEach(([key, value]) => {
        if (value) {
          selectedKey = key;
        }
      });
    }
    return selectedKey;
  }, [cart.option]);

  const filterDeliveriesByWeight = useCallback((keyCart) => {
    return (delivery) => {
      if (!cart.totalWeightByAddress || !cart.totalWeightByAddress[keyCart]) {
        return true;
      }

      const totalWeight = cart.totalWeightByAddress[keyCart];

      if (!delivery.prices || delivery.prices.length === 0) {
        console.warn(`[filterDeliveriesByWeight] L'option de livraison ${delivery.name} n'a pas de prix définis`);
        return false;
      }

      const maxWeightPrice = delivery.prices.reduce((max, price) =>
        (max === null || price.weight > max.weight) ? price : max, null);

      return totalWeight <= maxWeightPrice.weight;
    };
  }, [cart.totalWeightByAddress]);

  const isDeliveryDisabled = useCallback((delivery, keyCart) => {
    const selectedKey = getSelectedKeyOption(keyCart);
    if (selectedKey &&
      store?.physical_store?.card_options[selectedKey]?.forcedDelivery !== null &&
      store?.physical_store?.card_options[selectedKey]?.forcedDelivery !== delivery.id) {
      return true;
    }

    return false;
  }, [getSelectedKeyOption, store]);

  const generateCartItemsByAddress = useCallback(() => {
    if (!store) return;
  
    const itemsByAddress = {};
    const totalWeightByAddress = {};
  
    const baseCardWeight = calculateBaseCardWeight(store);
  
    cart.items.forEach((card, key) => {
      const address = createAddressKey(card);
  
      if (!itemsByAddress[address]) {
        itemsByAddress[address] = [];
      }
  
      card.key = key;
      itemsByAddress[address].push(card);
  
      if (!totalWeightByAddress[address]) {
        totalWeightByAddress[address] = 0;
      }
  
      totalWeightByAddress[address] += (card.qty || 1) * baseCardWeight;
    });
  
    const newDelivery = { ...cart.delivery };
    const newOption = { ...cart.option };
  
    Object.keys(cart.delivery).forEach(address => {
      if (!itemsByAddress[address]) {
        delete newDelivery[address];
      }
    });
  
    Object.keys(cart.option).forEach(address => {
      if (!itemsByAddress[address]) {
        delete newOption[address];
      }
    });
  
    Object.keys(itemsByAddress).forEach(address => {
      if (!newDelivery[address] && store?.physical_store?.deliveries?.length > 0) {
        newDelivery[address] = store.physical_store.deliveries[0];
      }
  
      if (!newOption[address]) {
        newOption[address] = {};
      }
  
      if (cart.option?.[address]) {
        Object.entries(cart.option[address]).forEach(([keyOption, isSelected]) => {
          if (isSelected) {
            let optionWeight = 0;
            
            if (Array.isArray(store?.physical_store?.card_options)) {
              const optionByAnchor = store.physical_store.card_options.find(
                opt => opt.anchor === keyOption
              );
              
              if (optionByAnchor) {
                optionWeight = optionByAnchor.weight || 0;
              } else {
                const index = parseInt(keyOption, 10);
                if (!isNaN(index) && index < store.physical_store.card_options.length) {
                  optionWeight = store.physical_store.card_options[index].weight || 0;
                }
              }
            } else if (store?.physical_store?.card_options?.[keyOption]) {
              optionWeight = store.physical_store.card_options[keyOption].weight || 0;
            }
            
            totalWeightByAddress[address] += optionWeight;
            
            let selectedOption = null;
            
            if (Array.isArray(store?.physical_store?.card_options)) {
              selectedOption = store.physical_store.card_options.find(
                opt => opt.anchor === keyOption
              );
              
              if (!selectedOption) {
                const index = parseInt(keyOption, 10);
                if (!isNaN(index) && index < store.physical_store.card_options.length) {
                  selectedOption = store.physical_store.card_options[index];
                }
              }
            }
            
            if (selectedOption && selectedOption.forcedDelivery) {
              const forcedDelivery = store.physical_store.deliveries.find(
                d => d.id === selectedOption.forcedDelivery
              );
              
              if (forcedDelivery) {
                newDelivery[address] = forcedDelivery;
              }
            }
          }
        });
      }
    });
  

    
    setCart(prev => ({
      ...prev,
      itemsByAddress,
      delivery: newDelivery,
      option: newOption,
      totalWeightByAddress
    }));
  
    calcCartTotals();
  }, [store, cart.items, cart.delivery, cart.option, calculateBaseCardWeight, calcCartTotals]);
  const generateCartItemsByAmount = useCallback(() => {
    const itemsByAmount = {};

    cart.items.forEach((card, key) => {
      const amount = card.amount;
      if (!itemsByAmount[amount]) {
        itemsByAmount[amount] = [];
      }
      card.key = key;
      itemsByAmount[amount].push(card);
    });

    setCart(prev => ({
      ...prev,
      itemsByAmount
    }));

    calcCartTotals();
  }, [cart.items, calcCartTotals]);

  const setDeliveryOptionDisponibility = useCallback((keyCart) => {
    const selectedKey = getSelectedKeyOption(keyCart);

    if (selectedKey) {
      let cardOption = null;

      if (Array.isArray(store?.physical_store?.card_options)) {
        cardOption = store.physical_store.card_options.find(
          opt => opt.anchor === selectedKey
        );
        if (!cardOption) {
          const index = parseInt(selectedKey, 10);
          if (!isNaN(index) && index < store.physical_store.card_options.length) {
            cardOption = store.physical_store.card_options[index];
          }
        }
      }

      if (cardOption && cardOption.forcedDelivery) {
        const forcedDelivery = store?.physical_store?.deliveries?.find(
          d => d.id === cardOption.forcedDelivery
        );

        if (forcedDelivery) {
          setCart(prev => ({
            ...prev,
            delivery: {
              ...prev.delivery,
              [keyCart]: forcedDelivery
            }
          }));

          return true;
        }
      }
    }

    return false;
  }, [getSelectedKeyOption, store]);

  const addCard = useCallback((card) => {
    setCart(prev => ({
      ...prev,
      items: [...prev.items, card]
    }));
  }, []);

  const deleteRequestCartItem = useCallback((itemIndex) => {
    setCart(prev => {
      const newItems = prev.items.filter((_, index) => index !== itemIndex);
      return {
        ...prev,
        items: newItems
      };
    });
  }, []);

  const selectDelivery = useCallback((keyCart, deliveryId) => {
    const selectedDelivery = store?.physical_store?.deliveries?.find(
      delivery => delivery.id === parseInt(deliveryId, 10)
    );

    if (selectedDelivery) {
      setCart(prev => ({
        ...prev,
        delivery: {
          ...prev.delivery,
          [keyCart]: selectedDelivery
        }
      }));

      calcCartTotals();
    }
  }, [store, calcCartTotals]);

  const selectOption = useCallback((keyCart, keyOption) => {
    const newOption = { ...cart.option };

    if (!newOption[keyCart]) {
      newOption[keyCart] = {};
    }

    const isCurrentlySelected = newOption[keyCart][keyOption];

    if (isCurrentlySelected) {
      newOption[keyCart][keyOption] = false;
      setCart(prev => ({
        ...prev,
        option: newOption
      }));

      setTimeout(() => {
        generateCartItemsByAddress();
      }, 10);

      return;
    }

    Object.keys(newOption[keyCart]).forEach(key => {
      newOption[keyCart][key] = false;
    });

    newOption[keyCart][keyOption] = true;

    let forcedDelivery = null;

    if (Array.isArray(store?.physical_store?.card_options)) {
      const index = parseInt(keyOption, 10);
      if (!isNaN(index) && index < store.physical_store.card_options.length) {
        const selectedOption = store.physical_store.card_options[index];

        if (selectedOption.forcedDelivery) {
          forcedDelivery = store.physical_store.deliveries.find(
            d => d.id === selectedOption.forcedDelivery
          );
        }
      }
    }

    if (forcedDelivery) {
      setCart(prev => ({
        ...prev,
        option: newOption,
        delivery: {
          ...prev.delivery,
          [keyCart]: forcedDelivery
        }
      }));
    } else {
      setCart(prev => ({
        ...prev,
        option: newOption
      }));
    }

    setTimeout(() => {
      generateCartItemsByAddress();
    }, 100); 
  }, [cart.option, store, generateCartItemsByAddress]);

  const copyCardToCart = useCallback((cardIndex, newData = null) => {
    const originalCard = cart.items[cardIndex];
    const newCard = {
      ...originalCard,
      firstNameReceiver: newData?.firstNameReceiver || '',
      lastNameReceiver: newData?.lastNameReceiver || '',
      emailReceiver: newData?.emailReceiver || '',
      isSelfDestination: newData?.isSelfDestination || false,
      dateTimeMode: newData?.dateTimeMode || 'now',
      datepicker: newData?.datepicker || '',
      timepicker: newData?.timepicker || '',
      sendDate: newData?.sendDate || '',
      sendDateTmp: newData?.sendDateTmp || '',
      cardLanguage: newData?.cardLanguage || originalCard.cardLanguage,
      qty: 1,
      momentSend: newData?.momentSend || 'now'
    };

    setCart(prev => ({
      ...prev,
      items: [...prev.items, newCard]
    }));
  }, [cart.items]);

  const cartLimitAcheived = useCallback((qtyToAdd = 0) => {
    if (selectedStore === 'physical') {
      const totalItems = cart.items.length + qtyToAdd;
      return store?.physical_store?.cart_items_limit > 0 &&
        totalItems > store.physical_store.cart_items_limit;
    } else {
      if (store?.cartItemsCountMax > 0) {
        const totalItems = cart.items.length + qtyToAdd;
        return totalItems > store.cartItemsCountMax;
      } else {
        return false;
      }
    }
  }, [cart.items.length, selectedStore, store]);

  const cartAmountLimitAcheived = useCallback((cardMontant, qty = 1) => {
    if (store?.cartAmountMax > 0) {
      const totalAfterAdd = totalAmounts + (Number(cardMontant) * qty);
      return totalAfterAdd > store.cartAmountMax;
    } else {
      return false;
    }
  }, [store, totalAmounts]);

  const cartMinNotReached = useCallback(() => {
    const cartItemsCountMin = store?.cartItemsCountMin;
    if (cartItemsCountMin != null && cartItemsCountMin > 0) {
      const currentCardsCount = cart.items.reduce((total, item) => {
        return total + (item.qty || 1);
      }, 0);
      return currentCardsCount < cartItemsCountMin;
    }
    return false;
  }, [store, cart.items]);

  const cartAmountMinNotReached = useCallback(() => {
    const cartAmountMin = store?.cartAmountMin;
    if (cartAmountMin != null && cartAmountMin > 0) {
      return totalAmounts < cartAmountMin;
    }
    return false;
  }, [store, totalAmounts]);

  const applyPromoCode = useCallback(async (code) => {
    if (!code || !store?.id) return;

    setPromoLoading(true);
    setPromoError(null);

    try {
      const graphics = process.env.REACT_APP_GRAPHICS_API_URL;
      const response = await fetch(
        `${graphics}/api/discount/check_promotional_code?id=${encodeURIComponent(store.id)}&promotional_code=${encodeURIComponent(code)}`
      );

      if (response.status === 404) {
        setPromoError('Code promotionnel invalide');
        setPromoDiscount(null);
        sessionStorage.removeItem('promoCode');
        sessionStorage.removeItem('promoDiscount');
        return;
      }

      if (!response.ok) {
        throw new Error('Erreur lors de la vérification du code');
      }

      const data = await response.json();

      if ( data.result && data.result.conditions) {
        setPromoCode(code);
        setPromoDiscount(data.result);
        sessionStorage.setItem('promoCode', code);
        sessionStorage.setItem('promoDiscount', JSON.stringify(data.result));
      } else {
        setPromoError('Code promotionnel invalide');
        setPromoDiscount(null);
        sessionStorage.removeItem('promoCode');
        sessionStorage.removeItem('promoDiscount');
      }
    } catch (error) {
      console.error('[applyPromoCode] Erreur:', error);
      setPromoError('Erreur lors de la vérification du code');
      setPromoDiscount(null);
      sessionStorage.removeItem('promoCode');
      sessionStorage.removeItem('promoDiscount');
    } finally {
      setPromoLoading(false);
    }
  }, [store?.id]);

  const removePromoCode = useCallback(() => {
    setPromoCode('');
    setPromoDiscount(null);
    setPromoError(null);
    sessionStorage.removeItem('promoCode');
    sessionStorage.removeItem('promoDiscount');
  }, []);

  // Recalculer les totaux quand la remise professionnelle change
  useEffect(() => {
    const proDiscount = store?.professionalUser?.discount || store?.proSignboardDiscount;
    if (proDiscount !== undefined && cart.items && cart.items.length > 0) {
      calcCartTotals();
    }
  }, [store?.professionalUser?.discount, store?.proSignboardDiscount, cart.items, calcCartTotals]);


  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        totalAmounts,
        totalAmountsAfterDiscount,
        totalPort,
        totalFinal,
        generateCartItemsByAddress,
        generateCartItemsByAmount,
        calcCartTotals,
        setDeliveryOptionDisponibility,
        getSelectedKeyOption,
        addCard,
        selectedStore,
        setSelectedStore,
        selectedStoreCards,
        applyDiscount,
        updateCard,
        haveDiscount,
        cartDiscountCondition,
        discountType,
        copyCardToCart,
        deleteRequestCartItem,
        filterDeliveriesByWeight,
        isDeliveryDisabled,
        calculateBaseCardWeight,
        selectDelivery,
        selectOption,
        cartItemsCountMax: store?.cartItemsCountMax || 0,
        cartAmountMax: store?.cartAmountMax || 0,
        cartLimitAcheived,
        cartAmountLimitAcheived,
        cartMinNotReached,
        cartAmountMinNotReached,
        promoCode,
        promoDiscount,
        promoError,
        promoLoading,
        applyPromoCode,
        removePromoCode
      }}
    >
      {children}
    </CartContext.Provider>
  );
};